/**
 * CX Leader Tasks — role === 'cx_leader' Tasks tab.
 *
 * Tab-based management views (monitoring only — assignment happens from the
 * Insights "Assign" sidebar). Every tab + count derives from a SINGLE
 * date-filtered dataset so card/tab count === table rows always.
 *
 * Backend:
 *   GET /api/role-insights/cx-leader?range=…   -> { listings: [...] }
 *   GET /api/qc/cx-leader-stats?range=…        -> { rows: {total,assigned,completed,pending} }
 *
 * CX Listings tabs : Total | Unassigned | Assigned | Completed | Under Review | Rejected
 * QC tabs          : Total | Assigned | Unassigned | Completed | Pending
 * Both sections carry a Today / All toggle (a record is never double-counted).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ExternalLink, ListChecks, ShieldCheck, CalendarClock, UserPlus, ClipboardList } from 'lucide-react';
import DateRangeFilter, { rangeParams } from '../../components/DateRangeFilter';
import MasterFormSection from '../../components/masterform/MasterFormSection';
import { useTaskTable, TaskSearchBar, TablePagination } from '../../components/tasks/TableControls';
import { userHasRole } from '../../utils/roleHelpers';

const SEARCH_KEYS = ['hotel_name', 'ota_name'];

const LISTING_STATUS_BADGE = {
  not_assigned: 'bg-gray-100 text-gray-600',
  assigned:     'bg-blue-100 text-blue-700',
  in_progress:  'bg-amber-100 text-amber-700',
  under_review: 'bg-amber-100 text-amber-700',
  rejected:     'bg-red-100 text-red-700',
  completed:    'bg-emerald-100 text-emerald-700',
};
const OVERDUE_PALETTE = {
  Late:     'bg-amber-100 text-amber-700 border-amber-200',
  Urgent:   'bg-orange-100 text-orange-700 border-orange-200',
  Critical: 'bg-rose-100 text-rose-700 border-rose-200',
};

// iter261 — Format an ISO timestamp as "DD/MM/YYYY HH:MM AM/PM" in the
// viewer's local timezone. Returns '—' for empty/invalid inputs.
const fmtCompletedAt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${dd}/${mm}/${yyyy} ${String(h).padStart(2, '0')}:${min} ${ampm}`;
};

const ScopeToggle = ({ scope, setScope, todayCount, allCount, testId }) => (
  <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm" data-testid={testId}>
    <button onClick={() => setScope('today')} data-testid={`${testId}-today`}
      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1 ${scope === 'today' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
      <CalendarClock size={13} /> Today ({todayCount})
    </button>
    <button onClick={() => setScope('all')} data-testid={`${testId}-all`}
      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${scope === 'all' ? 'bg-slate-700 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
      All ({allCount})
    </button>
  </div>
);

const TabBar = ({ tabs, active, setActive, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {tabs.map((t) => (
      <button key={t.key} onClick={() => setActive(t.key)} data-testid={`${testId}-${t.key}`}
        className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${active === t.key ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
        {t.label} <span className="ml-1 opacity-80">({t.count})</span>
      </button>
    ))}
  </div>
);

// Inline "Assign To" dropdown used in the Unassigned tabs.
const AssignSelect = ({ users, busy, onAssign, testId }) => (
  <div className="inline-flex items-center gap-1.5">
    <UserPlus size={13} className="text-teal-600 shrink-0" />
    <select
      defaultValue=""
      disabled={busy}
      onChange={(e) => onAssign(e.target.value)}
      data-testid={testId}
      className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white focus:ring-2 focus:ring-teal-200 outline-none disabled:opacity-50 max-w-[160px]">
      <option value="" disabled>{busy ? 'Assigning…' : 'Assign to…'}</option>
      {users.map((u) => (
        <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>
      ))}
    </select>
  </div>
);

const CXLeaderTasks = () => {
  const [data, setData] = useState(null);
  const [qc, setQc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qcUsers, setQcUsers] = useState([]);
  const [assigning, setAssigning] = useState(null); // `${lead}:${idx}` busy key
  const [searchParams, setSearchParams] = useSearchParams();
  // Preselect section/tab/scope/range deep-linked from CX Leader Insights cards.
  const [range, setRange] = useState(searchParams.get('range') || 'all');
  const [customFrom, setCustomFrom] = useState(searchParams.get('start') || '');
  const [customTo, setCustomTo] = useState(searchParams.get('end') || '');
  const initSection = (searchParams.get('section') || '').toLowerCase();
  const [listTab, setListTab] = useState(initSection === 'cx-listings' ? (searchParams.get('tab') || 'total') : 'total');
  const [listScope, setListScope] = useState('all');
  const [qcTab, setQcTab] = useState(initSection === 'qc' ? (searchParams.get('tab') || 'total') : 'total');
  const [qcScope, setQcScope] = useState('all');
  // Top-level section tabs (Master Forms default). Deep-links from Insights
  // cards preselect the matching section.
  const [topTab, setTopTab] = useState(
    initSection === 'cx-listings' ? 'cx-listings' : initSection === 'qc' ? 'qc' : 'master-forms'
  );

  const authHeader = () => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams(rangeParams(range, customFrom, customTo)).toString();
      const [resA, resB, resU] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/cx-leader?${qs}`, { headers: authHeader() }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qc/cx-leader-stats?${qs}`, { headers: authHeader() }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/lookup`, { headers: authHeader() }),
      ]);
      if (!resA.ok) throw new Error(`HTTP ${resA.status}`);
      setData(await resA.json());
      setQc(resB.ok ? await resB.json() : { rows: { total: [], assigned: [], completed: [], pending: [] } });
      if (resU.ok) {
        const all = await resU.json();
        // iter269 — Shared role helper; case-insensitive & multi-role
        // safe. Adding a new role requires zero changes here.
        setQcUsers((all || []).filter((u) => userHasRole(u, 'qc')));
      }
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  // ── Assignment (exactly the existing assignment endpoints) ──────────────
  const cxUsers = useMemo(() => data?.cx_users || [], [data]);

  const assignCx = async (leadId, otaIndex, assigneeId) => {
    if (!assigneeId) return;
    const key = `cx:${leadId}:${otaIndex}`;
    setAssigning(key);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/role-insights/cx-leader/lead/${leadId}/ota/${otaIndex}/assign`,
        { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ assignee_id: assigneeId }) },
      );
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.detail || `HTTP ${res.status}`); }
      toast.success('Listing assigned'); await load();
    } catch (e) { toast.error(e.message || 'Assign failed'); }
    finally { setAssigning(null); }
  };

  const assignQc = async (leadId, otaIndex, assigneeId) => {
    if (!assigneeId) return;
    const key = `qc:${leadId}:${otaIndex}`;
    setAssigning(key);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/qc/lead/${leadId}/ota/${otaIndex}/assign`,
        { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ assignee_id: assigneeId }) },
      );
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.detail || `HTTP ${res.status}`); }
      toast.success('QC assigned'); await load();
    } catch (e) { toast.error(e.message || 'QC assign failed'); }
    finally { setAssigning(null); }
  };

  const listings = useMemo(() => data?.listings || [], [data]);
  const byScope = (rows, scope) => (scope === 'today' ? rows.filter((r) => r.assigned_today) : rows);

  // CX Listings status buckets (non-overlapping; Total = sum of the rest).
  const listBuckets = useMemo(() => ({
    total:        listings,
    unassigned:   listings.filter((r) => r.status === 'not_assigned'),
    assigned:     listings.filter((r) => r.status === 'assigned' || r.status === 'in_progress'),
    completed:    listings.filter((r) => r.status === 'completed'),
    under_review: listings.filter((r) => r.status === 'under_review'),
    rejected:     listings.filter((r) => r.status === 'rejected'),
  }), [listings]);

  // QC buckets from the same dataset that feeds the Insights QC cards.
  const qcRows = qc?.rows || { total: [], assigned: [], completed: [], pending: [] };
  const qcBuckets = useMemo(() => ({
    total:      qcRows.total || [],
    assigned:   qcRows.assigned || [],
    unassigned: (qcRows.total || []).filter((r) => !r.assigned),
    completed:  qcRows.completed || [],
    pending:    qcRows.pending || [],
  }), [qcRows]);

  const listTabs = [
    { key: 'total', label: 'Total Listings', count: byScope(listBuckets.total, listScope).length },
    { key: 'unassigned', label: 'Unassigned', count: byScope(listBuckets.unassigned, listScope).length },
    { key: 'assigned', label: 'Assigned', count: byScope(listBuckets.assigned, listScope).length },
    { key: 'completed', label: 'Completed', count: byScope(listBuckets.completed, listScope).length },
    { key: 'under_review', label: 'Under Review', count: byScope(listBuckets.under_review, listScope).length },
    { key: 'rejected', label: 'Rejected', count: byScope(listBuckets.rejected, listScope).length },
  ];
  const qcTabs = [
    { key: 'total', label: 'Total QC', count: byScope(qcBuckets.total, qcScope).length },
    { key: 'assigned', label: 'Assigned QC', count: byScope(qcBuckets.assigned, qcScope).length },
    { key: 'unassigned', label: 'Unassigned QC', count: byScope(qcBuckets.unassigned, qcScope).length },
    { key: 'completed', label: 'Completed QC', count: byScope(qcBuckets.completed, qcScope).length },
    { key: 'pending', label: 'Pending QC', count: byScope(qcBuckets.pending, qcScope).length },
  ];

  const shownListings = byScope(listBuckets[listTab] || [], listScope);
  const shownQc = byScope(qcBuckets[qcTab] || [], qcScope);

  // Independent property search + pagination per table (display only — the
  // tab/scope counts above stay derived from the full buckets).
  const listTable = useTaskTable(shownListings, { keys: SEARCH_KEYS, pageSize: 10, resetKey: `${listTab}:${listScope}` });
  const qcTable = useTaskTable(shownQc, { keys: SEARCH_KEYS, pageSize: 10, resetKey: `${qcTab}:${qcScope}` });

  return (
    <div className="space-y-6" data-testid="cx-leader-tasks">
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">CX Leader Tasks</h2>
          <p className="text-sm text-gray-500 mt-0.5">Monitor CX listings and QC quality checks by status.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          data-testid="cxl-tasks-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="cxl-tasks-error">{error}</div>}

      <DateRangeFilter value={range} onChange={setRange} customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} testId="cxl-tasks-date-filter" />

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <>
          {/* Top-level section tabs — Master Forms is the default. */}
          <div className="flex flex-wrap gap-2 bg-white rounded-xl border border-gray-200 shadow-sm p-2" data-testid="cxl-tasks-top-tabs">
            {[
              { key: 'master-forms', label: 'Master Forms', icon: ClipboardList },
              { key: 'cx-listings', label: 'CX Listings', icon: ListChecks },
              { key: 'qc', label: 'QC Quality Checks', icon: ShieldCheck },
            ].map((t) => {
              const Icon = t.icon;
              const active = topTab === t.key;
              return (
                <button key={t.key} onClick={() => setTopTab(t.key)} data-testid={`cxl-tasks-top-tab-${t.key}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border transition-colors ${active ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Phase 4 — Master Forms (Onboarding Form follow-up) */}
          <div className={topTab === 'master-forms' ? '' : 'hidden'}>
            <MasterFormSection mode="manager" range={range} customFrom={customFrom} customTo={customTo}
              initialTab={initSection === 'master-forms' ? (searchParams.get('tab') || 'total') : 'total'}
              testId="cxl-master-forms" />
          </div>

          {/* CX Listings */}
          <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${topTab === 'cx-listings' ? '' : 'hidden'}`} data-testid="cxl-tasks-listings-section">
            <div className="px-5 py-3.5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><ListChecks size={16} className="text-teal-600" /> CX Listings</h3>
              <ScopeToggle scope={listScope} setScope={setListScope} todayCount={byScope(listBuckets[listTab] || [], 'today').length} allCount={(listBuckets[listTab] || []).length} testId="cxl-tasks-listings-scope" />
            </div>
            <div className="px-5 py-3 border-b border-gray-100">
              <TabBar tabs={listTabs} active={listTab} setActive={setListTab} testId="cxl-tasks-listings-tabs" />
            </div>
            <div className="px-5 py-3 border-b border-gray-100">
              <TaskSearchBar query={listTable.query} setQuery={listTable.setQuery} testId="cxl-tasks-listings-search" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cxl-tasks-listings-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Hotel</th>
                    <th className="px-4 py-2.5 text-left">OTA</th>
                    <th className="px-4 py-2.5 text-left">Status</th>
                    <th className="px-4 py-2.5 text-left">CX</th>
                    <th className="px-4 py-2.5 text-left">Assigned</th>
                    <th className="px-4 py-2.5 text-left">Completed On</th>
                    <th className="px-4 py-2.5 text-left">Duration</th>
                    <th className="px-4 py-2.5 text-left">Overdue</th>
                    <th className="px-4 py-2.5 text-left">URL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {listTable.pageRows.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-xs">No listings in this view.</td></tr>
                  )}
                  {listTable.pageRows.map((r) => {
                    const cls = LISTING_STATUS_BADGE[r.status] || LISTING_STATUS_BADGE.not_assigned;
                    const ov = (r.overdue_label || '').trim();
                    const isCompleted = r.status === 'completed';
                    return (
                      <tr key={`${r.lead_id}-${r.ota_index}`} className="hover:bg-gray-50" data-testid={`cxl-tasks-listing-row-${r.lead_id}-${r.ota_index}`}>
                        <td className="px-4 py-2.5 font-medium text-gray-800">{r.hotel_name || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-700">{r.ota_name || '—'}</td>
                        <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{(r.status || '').replace('_', ' ')}</span></td>
                        <td className="px-4 py-2.5 text-gray-600 text-xs">
                          {listTab === 'unassigned' ? (
                            <AssignSelect users={cxUsers} busy={assigning === `cx:${r.lead_id}:${r.ota_index}`}
                              onAssign={(uid) => assignCx(r.lead_id, r.ota_index, uid)}
                              testId={`cxl-tasks-assign-cx-${r.lead_id}-${r.ota_index}`} />
                          ) : (r.cx_name || '—')}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{r.assigned_at ? new Date(r.assigned_at).toLocaleString() : '—'}</td>
                        {/* iter261 — Completed On + Duration columns, populated only for completed listings. */}
                        <td className="px-4 py-2.5 text-gray-600 text-xs whitespace-nowrap" data-testid={`cxl-tasks-listing-completed-at-${r.lead_id}-${r.ota_index}`}>
                          {isCompleted ? fmtCompletedAt(r.completed_at) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 text-xs whitespace-nowrap" data-testid={`cxl-tasks-listing-duration-${r.lead_id}-${r.ota_index}`}>
                          {isCompleted ? (r.completion_duration || <span className="text-gray-300">—</span>) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-2.5">{ov ? <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${OVERDUE_PALETTE[ov] || ''}`}>{ov}{r.overdue_time && <span className="ml-1 text-[10px] opacity-80">· {r.overdue_time}</span>}</span> : <span className="text-gray-300 text-xs">—</span>}</td>
                        <td className="px-4 py-2.5 text-xs">{r.listing_url ? <a href={r.listing_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-1"><ExternalLink size={11} /> open</a> : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TablePagination page={listTable.page} totalPages={listTable.totalPages} setPage={listTable.setPage} total={listTable.total} pageSize={listTable.pageSize} testId="cxl-tasks-listings-pagination" />
          </div>

          {/* QC Quality Checks */}
          <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${topTab === 'qc' ? '' : 'hidden'}`} data-testid="cxl-tasks-qc-section">
            <div className="px-5 py-3.5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><ShieldCheck size={16} className="text-fuchsia-600" /> QC Quality Checks</h3>
              <ScopeToggle scope={qcScope} setScope={setQcScope} todayCount={byScope(qcBuckets[qcTab] || [], 'today').length} allCount={(qcBuckets[qcTab] || []).length} testId="cxl-tasks-qc-scope" />
            </div>
            <div className="px-5 py-3 border-b border-gray-100">
              <TabBar tabs={qcTabs} active={qcTab} setActive={setQcTab} testId="cxl-tasks-qc-tabs" />
            </div>
            <div className="px-5 py-3 border-b border-gray-100">
              <TaskSearchBar query={qcTable.query} setQuery={qcTable.setQuery} testId="cxl-tasks-qc-search" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cxl-tasks-qc-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Hotel</th>
                    <th className="px-4 py-2.5 text-left">OTA</th>
                    <th className="px-4 py-2.5 text-left">QC Status</th>
                    <th className="px-4 py-2.5 text-left">Assign To</th>
                    <th className="px-4 py-2.5 text-left">Overdue</th>
                    <th className="px-4 py-2.5 text-left">URL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {qcTable.pageRows.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-xs">No QC tasks in this view.</td></tr>
                  )}
                  {qcTable.pageRows.map((r) => {
                    const ov = (r.overdue_label || '').trim();
                    return (
                      <tr key={`${r.lead_id}-${r.ota_index}`} className="hover:bg-gray-50" data-testid={`cxl-tasks-qc-row-${r.lead_id}-${r.ota_index}`}>
                        <td className="px-4 py-2.5 font-medium text-gray-800">{r.hotel_name || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-700">{r.ota_name || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-700 text-xs">{r.status || '—'}</td>
                        <td className="px-4 py-2.5">
                          {qcTab === 'unassigned' ? (
                            <AssignSelect users={qcUsers} busy={assigning === `qc:${r.lead_id}:${r.ota_index}`}
                              onAssign={(uid) => assignQc(r.lead_id, r.ota_index, uid)}
                              testId={`cxl-tasks-assign-qc-${r.lead_id}-${r.ota_index}`} />
                          ) : (
                            <span className="text-gray-700 text-xs font-medium" data-testid={`cxl-tasks-qc-assigned-name-${r.lead_id}-${r.ota_index}`}>{r.qc_assigned_to_name || '—'}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">{ov ? <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${OVERDUE_PALETTE[ov] || ''}`}>{ov}</span> : <span className="text-gray-300 text-xs">—</span>}</td>
                        <td className="px-4 py-2.5 text-xs">{r.listing_url ? <a href={r.listing_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-1"><ExternalLink size={11} /> open</a> : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TablePagination page={qcTable.page} totalPages={qcTable.totalPages} setPage={qcTable.setPage} total={qcTable.total} pageSize={qcTable.pageSize} testId="cxl-tasks-qc-pagination" />
          </div>
        </>
      )}
    </div>
  );
};

export default CXLeaderTasks;
